/* ---------------------------------------------------------------------------
   Talk to a booth expert — opens the booking calendar over the page.

   Every "Talk to a booth expert" control on the site is an ordinary link to
   Calendly. This turns those links into a panel that opens on top of the page
   the buyer is already on, so a half-finished stand configuration is never
   thrown away to go and pick a time.

   WHY IT USED TO FEEL SLOW
   -----------------------
   The calendar is Calendly's, served from Calendly's machines, and nothing on
   our side can change that. What we can change is how much of the journey is
   already done before the button is pressed, and whether anything happens on
   the screen while the rest of it finishes.

   The old version did none of it in advance: the first hover opened a DNS
   lookup, a TLS handshake, a stylesheet, a 90 KB script and then an iframe
   that fetches a whole application and an availability feed. A click with no
   hover before it — which is every click on a touch screen — paid for all of
   that at once, with a dead button for three or four seconds.

   Three things fixed it.

     1. The connection is opened the moment this file runs. DNS and TLS to
        both Calendly hosts are done long before anyone reaches for the button,
        which is most of a second on a mobile network.

     2. The widget is fetched when the browser goes idle, not on hover. By the
        time any human has read the page it is in cache, so the click has
        nothing left to download. Skipped on a metered or 2G connection, where
        it falls back to the old hover behaviour and costs nobody a byte.

     3. The panel appears on the very next frame after the click, whatever is
        or is not loaded, carrying our own mark and a spinner. It is lifted
        the moment Calendly's iframe reports it has painted. A buyer never
        presses a button and watches nothing happen.

   If the widget never arrives at all, the link does what it always did and
   goes to the booking page. A buyer who wants to book can always book.
--------------------------------------------------------------------------- */
(function () {
  "use strict";

  var WIDGET_CSS = "https://assets.calendly.com/assets/external/widget.css";
  var WIDGET_JS  = "https://assets.calendly.com/assets/external/widget.js";
  var GIVE_UP_MS = 8000;
  var VEIL_MAX_MS = 12000;

  var IDLE = 0, LOADING = 1, READY = 2, FAILED = 3;
  var state = IDLE;
  var pending = [];
  var veilEl = null;
  var veilTimer = null;

  /* Is this anchor a Calendly link? Matched on the parsed hostname rather than
     on the string, so a product called "calendly" could never be mistaken. */
  function isBooking(a) {
    if (!a || a.tagName !== "A" || !a.getAttribute("href")) return false;
    try {
      var h = new URL(a.href, location.href).hostname.toLowerCase();
      return h === "calendly.com" || h.slice(-13) === ".calendly.com";
    } catch (e) { return false; }
  }

  function tag(name, attrs) {
    var el = document.createElement(name);
    for (var k in attrs) if (attrs.hasOwnProperty(k)) el.setAttribute(k, attrs[k]);
    return el;
  }

  /* Metered, 2G, or the data-saver switch: do nothing speculative. */
  function thrifty() {
    var c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (!c) return false;
    if (c.saveData) return true;
    var t = c.effectiveType || "";
    return t === "slow-2g" || t === "2g";
  }

  /* ----------------------------------------------------------- our own panel */
  /* Shown on the next frame after the click and taken away when Calendly has
     painted. It sits above Calendly's overlay on purpose: their popup opens as
     an empty white box while the iframe loads, and an empty white box is worse
     than a spinner. */
  function skinVeil() {
    if (document.getElementById("mp-cal-veilcss")) return;
    var css = document.createElement("style");
    css.id = "mp-cal-veilcss";
    css.textContent = [
      "#mp-cal-veil{position:fixed;inset:0;z-index:10050;display:flex;",
        "align-items:center;justify-content:center;",
        "background:rgba(16,21,18,.38);",
        "-webkit-backdrop-filter:saturate(1.08) blur(2px);",
        "backdrop-filter:saturate(1.08) blur(2px);",
        "animation:mpcv-in .16s ease both}",
      "#mp-cal-veil.out{animation:mpcv-out .2s ease both;pointer-events:none}",
      /* Once Calendly's own overlay is in the page it is doing the dimming,
         so ours stops: two translucent veils stacked turned their white panel
         grey. The spinner stays, floating over their empty box, until the
         calendar inside it has actually drawn. */
      "#mp-cal-veil.bare{background:transparent;",
        "-webkit-backdrop-filter:none;backdrop-filter:none}",
      "@keyframes mpcv-in{from{opacity:0}to{opacity:1}}",
      "@keyframes mpcv-out{from{opacity:1}to{opacity:0}}",
      "#mp-cal-veil .b{display:flex;flex-direction:column;align-items:center;gap:14px;",
        "padding:30px 38px;border-radius:3px;background:#fff;",
        "box-shadow:0 2px 6px rgba(16,21,18,.10),0 28px 70px rgba(16,21,18,.26)}",
      "#mp-cal-veil .r{width:26px;height:26px;border-radius:50%;",
        "border:2px solid rgba(47,93,63,.22);border-top-color:#2F5D3F;",
        "animation:mpcv-spin .7s linear infinite}",
      "@keyframes mpcv-spin{to{transform:rotate(360deg)}}",
      "#mp-cal-veil .t{font-family:ui-monospace,SFMono-Regular,Menlo,monospace;",
        "font-size:.625rem;letter-spacing:.2em;text-transform:uppercase;color:#77848B}",
      "@media (prefers-reduced-motion:reduce){",
        "#mp-cal-veil,#mp-cal-veil.out{animation:none}",
        "#mp-cal-veil .r{animation-duration:1.6s}}"
    ].join("");
    document.head.appendChild(css);
  }

  function veilUp(label) {
    if (veilEl) return;
    skinVeil();
    veilEl = document.createElement("div");
    veilEl.id = "mp-cal-veil";
    veilEl.setAttribute("role", "status");
    veilEl.setAttribute("aria-live", "polite");
    veilEl.innerHTML = '<div class="b"><div class="r"></div><div class="t"></div></div>';
    veilEl.querySelector(".t").textContent = label || "Opening the calendar";
    document.body.appendChild(veilEl);
    veilTimer = setTimeout(veilDown, VEIL_MAX_MS);
  }

  function veilDown() {
    if (!veilEl) return;
    clearTimeout(veilTimer);
    var el = veilEl;
    veilEl = null;
    el.classList.add("out");
    setTimeout(function () { if (el.parentNode) el.parentNode.removeChild(el); }, 220);
  }

  /* Calendly's iframe tells us when it has drawn the event type. Belt and
     braces: we also watch the iframe's own load event, because the message
     only fires on their newer embeds. */
  window.addEventListener("message", function (e) {
    if (!veilEl) return;
    var o = String(e.origin || "");
    if (o.indexOf("calendly.com") === -1) return;
    var d = e.data;
    if (d && typeof d.event === "string" && d.event.indexOf("calendly.") === 0) veilDown();
  }, false);

  function watchFrame() {
    var tries = 0;
    (function look() {
      if (!veilEl) return;
      if (document.querySelector(".calendly-overlay")) veilEl.classList.add("bare");
      var f = document.querySelector(".calendly-overlay iframe, .calendly-popup-content iframe");
      if (f) {
        f.addEventListener("load", function () { setTimeout(veilDown, 260); }, { once: true });
        /* Already loaded by the time we found it. */
        try { if (f.contentDocument && f.contentDocument.readyState === "complete") setTimeout(veilDown, 260); } catch (err) {}
        return;
      }
      if (++tries > 120) return;
      requestAnimationFrame(look);
    }());
  }

  /* --------------------------------------------------------- Calendly's skin */
  function paint() {
    if (document.getElementById("mp-cal-skin")) return;
    var css = document.createElement("style");
    css.id = "mp-cal-skin";
    css.textContent = [
      ".calendly-overlay{",
        "background-color:rgba(16,21,18,.38)!important;",
        "-webkit-backdrop-filter:saturate(1.08) blur(2px);",
        "backdrop-filter:saturate(1.08) blur(2px);",
        "animation:mpcal-in .22s ease both}",
      "@keyframes mpcal-in{from{opacity:0}to{opacity:1}}",
      /* Width and height only. Calendly centres this element with a transform
         of its own, so nothing here may set transform or animation on it — an
         animation that ends at transform:none takes the centring with it and
         drops the panel into the bottom-right corner. The entrance is played
         on the content inside instead, which carries no transform. */
      ".calendly-overlay .calendly-popup{",
        "min-width:0;width:min(1000px,calc(100vw - 48px));",
        "border-radius:3px;overflow:hidden;",
        "box-shadow:0 2px 6px rgba(16,21,18,.10),0 28px 70px rgba(16,21,18,.26)}",
      ".calendly-overlay .calendly-popup-content{",
        "border-radius:3px;",
        "animation:mpcal-rise .3s cubic-bezier(.22,.61,.36,1) both}",
      "@keyframes mpcal-rise{from{opacity:0}to{opacity:1}}",
      /* Calendly's close control is a bare white glyph, which disappears over a
         pale page. A chip behind it keeps it findable on any background. */
      ".calendly-overlay .calendly-popup-close{",
        "width:34px;height:34px;background-size:14px 14px;background-position:center;",
        "background-color:rgba(16,21,18,.62);border-radius:50%;",
        "top:18px;right:18px;opacity:1}",
      ".calendly-overlay .calendly-popup-close:hover{background-color:rgba(16,21,18,.86)}",
      /* On a phone the panel is a full sheet. All four edges are pinned rather
         than a height set, because Calendly holds a strip at the top for the
         close control and a 100vh panel under it hangs off the bottom of the
         screen. Pinning the edges also makes the centring transform redundant,
         which is the only reason it is safe to clear it here. */
      "@media (max-width:760px){",
        ".calendly-overlay .calendly-popup{",
          "top:50px;left:0;right:0;bottom:0;",
          "width:auto;height:auto;max-width:none;max-height:none;",
          "transform:none;border-radius:0}",
        ".calendly-overlay .calendly-popup-close{top:9px;right:12px;width:30px;height:30px}}",
      "@media (prefers-reduced-motion:reduce){",
        ".calendly-overlay,.calendly-overlay .calendly-popup-content{animation:none}}"
    ].join("");
    document.head.appendChild(css);
  }

  /* ------------------------------------------------------------- the fetching */
  function connect() {
    if (document.getElementById("mp-cal-pre")) return;
    var m = document.createElement("meta");
    m.id = "mp-cal-pre";
    document.head.appendChild(m);
    document.head.appendChild(tag("link", { rel: "preconnect", href: "https://assets.calendly.com", crossorigin: "" }));
    document.head.appendChild(tag("link", { rel: "preconnect", href: "https://calendly.com" }));
    document.head.appendChild(tag("link", { rel: "dns-prefetch", href: "https://assets.calendly.com" }));
    document.head.appendChild(tag("link", { rel: "dns-prefetch", href: "https://calendly.com" }));
  }

  /* The booking page itself, pulled into cache so the iframe has a head start
     on the one document it cannot begin without. */
  function prefetchBooking() {
    var a = document.querySelector("a[href]");
    var all = document.querySelectorAll("a[href]");
    for (var i = 0; i < all.length; i++) {
      if (isBooking(all[i])) {
        document.head.appendChild(tag("link", { rel: "prefetch", href: all[i].href, as: "document" }));
        return;
      }
    }
  }

  function warm() {
    if (state !== IDLE) return;
    state = LOADING;
    connect();
    prefetchBooking();
    document.head.appendChild(tag("link", { rel: "stylesheet", href: WIDGET_CSS }));

    var s = document.createElement("script");
    s.src = WIDGET_JS;
    s.async = true;
    s.onload = function () {
      state = window.Calendly && window.Calendly.initPopupWidget ? READY : FAILED;
      paint();
      flush();
    };
    s.onerror = function () { state = FAILED; flush(); };
    document.head.appendChild(s);

    setTimeout(function () { if (state === LOADING) { state = FAILED; flush(); } }, GIVE_UP_MS);
  }

  function flush() {
    var queue = pending;
    pending = [];
    for (var i = 0; i < queue.length; i++) queue[i]();
  }

  function show(url, trigger) {
    if (trigger) trigger.removeAttribute("aria-busy");
    if (state === READY) {
      paint();
      window.Calendly.initPopupWidget({ url: url });
      watchFrame();
      if (window.dataLayer && typeof window.dataLayer.push === "function") {
        window.dataLayer.push({ event: "booking_panel_opened", booking_url: url });
      }
    } else {
      /* The widget never arrived. Send them to the booking page itself rather
         than leave a dead button. */
      veilDown();
      window.location.href = url;
    }
  }

  function onClick(e) {
    if (e.defaultPrevented) return;
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

    var a = e.target && e.target.closest ? e.target.closest("a") : null;
    if (!isBooking(a)) return;

    e.preventDefault();
    var url = a.href;

    /* Before anything else, and before anything can be waited on. */
    veilUp(a.getAttribute("data-booking-label"));

    if (state === READY || state === FAILED) { show(url, a); return; }

    a.setAttribute("aria-busy", "true");
    pending.push(function () { show(url, a); });
    warm();
  }

  function onIntent(e) {
    var a = e.target && e.target.closest ? e.target.closest("a") : null;
    if (isBooking(a)) warm();
  }

  document.addEventListener("click", onClick, false);
  document.addEventListener("pointerover", onIntent, { passive: true });
  document.addEventListener("focusin", onIntent, true);
  document.addEventListener("touchstart", onIntent, { passive: true });

  document.addEventListener("keydown", function (e) {
    if (e.key !== "Escape") return;
    if (veilEl) veilDown();
    if (!document.querySelector(".calendly-overlay")) return;
    if (window.Calendly && window.Calendly.closePopupWidget) window.Calendly.closePopupWidget();
  });

  /* --------------------------------------------------------------- the shine */
  /* Every booking control on the site is found by the same hostname test the
     click handler uses, so the treatment lands on all of them at once — the
     header button, the hero link, the closing band, the FAQ, the footer menus
     — and on any new one without a line of markup.

     Three dressings, because these controls are not one thing. A white
     specular sweep is invisible on a white button and garish on a bare text
     link, so the background each control actually has is measured and it is
     given the one that suits it:

       solid    a filled green button — a white sweep and a slow ring pulse
       outline  a white button with a rule round it — a green-tinted sweep
                and a border that warms on the beat
       link     bare text — no sweep at all; an amber rule that wipes in
                under it and an arrow that steps forward

     The beat is every six seconds and the sweep itself lasts a second. Fast
     enough to be caught out of the corner of the eye, slow enough that nobody
     sitting on the page feels nagged. Hover plays it immediately. With motion
     turned off at the operating system, none of it runs. */
  function luminance(c) {
    var m = /rgba?\(([^)]+)\)/.exec(c || "");
    if (!m) return 1;
    var p = m[1].split(",");
    var a = p.length > 3 ? parseFloat(p[3]) : 1;
    if (a < 0.06) return -1;              /* effectively transparent */
    var r = +p[0], g = +p[1], b = +p[2];
    return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
  }

  function skinShine() {
    if (document.getElementById("mp-xpert-css")) return;
    var css = document.createElement("style");
    css.id = "mp-xpert-css";
    css.textContent = [
      ".mp-xpert{position:relative;isolation:isolate}",
      ".mp-xpert.is-solid,.mp-xpert.is-outline{overflow:hidden}",

      /* the travelling highlight */
      ".mp-xpert.is-solid::after,.mp-xpert.is-outline::after{",
        "content:'';position:absolute;z-index:1;pointer-events:none;",
        "top:-60%;bottom:-60%;width:42%;left:-70%;",
        "transform:skewX(-20deg);",
        "animation:mpx-sweep 6s cubic-bezier(.4,0,.2,1) infinite}",
      ".mp-xpert.is-solid::after{",
        "background:linear-gradient(100deg,rgba(255,255,255,0),rgba(255,255,255,.55),rgba(255,255,255,0))}",
      ".mp-xpert.is-outline::after{",
        "background:linear-gradient(100deg,rgba(47,93,63,0),rgba(47,93,63,.13),rgba(47,93,63,0))}",
      "@keyframes mpx-sweep{0%{left:-70%}16%{left:150%}100%{left:150%}}",

      /* the ring, on the filled one only */
      ".mp-xpert.is-solid{animation:mpx-ring 6s ease-out infinite}",
      "@keyframes mpx-ring{",
        "0%{box-shadow:0 0 0 0 rgba(47,93,63,.42)}",
        "14%{box-shadow:0 0 0 7px rgba(47,93,63,0)}",
        "100%{box-shadow:0 0 0 0 rgba(47,93,63,0)}}",
      ".mp-xpert.is-outline{animation:mpx-edge 6s ease-out infinite}",
      "@keyframes mpx-edge{",
        "0%{border-color:var(--mps-brand,#2F5D3F)}",
        "22%{border-color:var(--mps-brand,#2F5D3F)}",
        "55%{border-color:var(--mps-edge,#E1E6E4)}",
        "100%{border-color:var(--mps-edge,#E1E6E4)}}",

      /* hover plays it at once and lifts the control */
      "@media (hover:hover){",
        ".mp-xpert.is-solid:hover,.mp-xpert.is-outline:hover{",
          "transform:translateY(-1px)}",
        ".mp-xpert.is-solid:hover::after,.mp-xpert.is-outline:hover::after{",
          "animation:mpx-sweep 1.1s cubic-bezier(.4,0,.2,1)}",
        ".mp-xpert.is-link:hover .mp-xpert-rule{transform:scaleX(1)}}",

      /* the bare text link */
      ".mp-xpert.is-link{overflow:visible}",
      ".mp-xpert-rule{position:absolute;left:0;right:0;bottom:-3px;height:2px;",
        "border-radius:2px;background:linear-gradient(90deg,#EBC451,#2F5D3F);",
        "transform:scaleX(0);transform-origin:left center;",
        "transition:transform .34s cubic-bezier(.22,.61,.36,1);",
        "animation:mpx-wipe 6s ease-in-out infinite;pointer-events:none}",
      "@keyframes mpx-wipe{",
        "0%{transform:scaleX(0);transform-origin:left center}",
        "11%{transform:scaleX(1);transform-origin:left center}",
        "26%{transform:scaleX(1);transform-origin:right center}",
        "38%{transform:scaleX(0);transform-origin:right center}",
        "100%{transform:scaleX(0);transform-origin:right center}}",
      ".mp-xpert.is-link svg,.mp-xpert.is-link .mpx-arrow{",
        "animation:mpx-nudge 6s ease-in-out infinite}",
      "@keyframes mpx-nudge{0%,8%{transform:translateX(0)}",
        "16%{transform:translateX(4px)}24%,100%{transform:translateX(0)}}",

      "@media (prefers-reduced-motion:reduce){",
        ".mp-xpert.is-solid,.mp-xpert.is-outline,",
        ".mp-xpert.is-solid::after,.mp-xpert.is-outline::after,",
        ".mp-xpert-rule,.mp-xpert.is-link svg,.mp-xpert.is-link .mpx-arrow{",
          "animation:none}",
        ".mp-xpert.is-solid:hover,.mp-xpert.is-outline:hover{transform:none}}"
    ].join("");
    document.head.appendChild(css);
  }

  function dress() {
    var all = document.querySelectorAll("a[href]");
    var found = 0;
    for (var i = 0; i < all.length; i++) {
      var a = all[i];
      if (!isBooking(a)) continue;
      if (a.classList.contains("mp-xpert")) continue;
      /* A control inside the Calendly panel itself is left alone. */
      if (a.closest && a.closest(".calendly-overlay")) continue;

      var cs = getComputedStyle(a);
      var lum = luminance(cs.backgroundColor);
      var bordered = parseFloat(cs.borderTopWidth || 0) >= 1 ||
                     parseFloat(cs.borderBottomWidth || 0) >= 1;
      var kind;
      if (lum < 0) kind = bordered ? "is-outline" : "is-link";
      else if (lum > 0.6) kind = "is-outline";
      else kind = "is-solid";

      /* A control laid out as plain inline text cannot hold an absolutely
         positioned rule without the browser putting it in the wrong place. */
      if (kind === "is-link" && cs.display === "inline") a.style.display = "inline-block";

      a.classList.add("mp-xpert", kind);
      if (kind === "is-link" && !a.querySelector(".mp-xpert-rule")) {
        var r = document.createElement("span");
        r.className = "mp-xpert-rule";
        r.setAttribute("aria-hidden", "true");
        a.appendChild(r);
      }
      found++;
    }
    return found;
  }

  function shine() {
    skinShine();
    dress();
    /* Menus, drawers and the cart render after this runs, so new controls are
       dressed as they appear rather than missed. Throttled to a frame. */
    if (!("MutationObserver" in window)) return;
    var queued = false;
    new MutationObserver(function () {
      if (queued) return;
      queued = true;
      requestAnimationFrame(function () { queued = false; dress(); });
    }).observe(document.body, { childList: true, subtree: true });
  }

  /* ------------------------------------------------------------- at the start */
  /* Nothing is fetched here any more.

     This used to call connect() on every page and then, on the first idle
     moment, warm() — which pulls Calendly's widget stylesheet and script.
     The launch audit measured the cost of that speculation: 1,276.5 KiB of
     booking CSS transferring on the home page of a visitor who had not asked
     to book anything, on a page already over its mobile budget. It also put
     four preconnect and dns-prefetch origins in the head of every page for a
     third party most visitors never touch.

     The intent handlers below already catch the real signal — a pointer over
     a booking link, a focus on one, a finger on one — and warm() runs then,
     which is early enough to feel instant and late enough to cost nothing.
     connect() moved inside warm() for the same reason. */

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", shine, { once: true });
  } else {
    shine();
  }
})();
